import { Capacitor } from '@capacitor/core';
import { initializeFirestore } from 'firebase/firestore';
import { FirebaseApp, initializeApp } from 'firebase/app';

import { hasCapability } from 'src/platform';
import { FIREBASE_API } from 'src/config-global';
import { isOfflineDemoActiveAtBoot } from 'src/features/offline-demo/boot';

// ----------------------------------------------------------------------

// Cached Firebase app instance
let cachedFirebaseApp: FirebaseApp | null = null;

/**
 * iOS routes every WebView GET through CapacitorHttp's native proxy, which
 * buffers the whole response (URLSession completion handler), so Firestore's
 * streaming listen channel cannot stream there. Long-polling closes each
 * response as soon as data is sent, which survives the buffering. Must run
 * before the first `getFirestore(app)`; every call site resolves the app
 * through `getFirebaseWebApp()` first. See docs/KDS.md.
 */
function configureNativeFirestore(app: FirebaseApp): void {
  try {
    if (Capacitor.getPlatform() === 'ios') {
      initializeFirestore(app, { experimentalForceLongPolling: true });
    }
  } catch (error) {
    console.warn('[Firebase] Firestore transport settings were not applied', error);
  }
}

/**
 * Get Firebase app instance for web SDK features (Firestore, RemoteConfig, Auth, etc.)
 *
 * Selected by `capabilities.firebaseWebApp`:
 * - 'default'            — web AND electron: the default app, so the web SDK,
 *   Firestore and RTDB share one auth state (electron signs in via the web SDK).
 * - 'named-web-services' — iOS/Android: a named app beside the native Firebase
 *   SDK; auth state is synced manually via signInWithCustomToken.
 *
 * Root cause this fixes on electron: the native auth plugin's web fallback
 * calls getAuth() with no app argument, i.e. the [DEFAULT] app — which a
 * 'named-web-services'-only process never creates, so every call fails with
 * `auth/no-app`.
 *
 * IMPORTANT: This function should be used for ALL Firebase web SDK operations
 * (Auth, Firestore, RemoteConfig, Storage, etc.) to ensure consistent auth state.
 */
export function getFirebaseWebApp(): FirebaseApp {
  if (isOfflineDemoActiveAtBoot()) {
    throw new Error('Firebase is unavailable during an Offline Demo boot');
  }

  if (cachedFirebaseApp) {
    return cachedFirebaseApp;
  }

  if (hasCapability('firebaseWebApp') === 'named-web-services') {
    // Separate from the native Firebase SDK (initialized by google-services.json).
    console.log('[Firebase] Creating web-services app for native platform');
    const app = initializeApp(FIREBASE_API, 'web-services');
    configureNativeFirestore(app);
    cachedFirebaseApp = app;
  } else {
    // Default app (no name) for session persistence compatibility.
    console.log('[Firebase] Creating default app');
    cachedFirebaseApp = initializeApp(FIREBASE_API);
  }

  return cachedFirebaseApp;
}

// Legacy export for backward compatibility (not used anymore)
// All code should use getFirebaseWebApp() instead
export const firebaseAppOnlyForWeb: FirebaseApp | null = null;
